import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon, Paperclip, Trash2 } from 'lucide-react';
import { Button } from '@/components/ds';

/** Mirrors the server's allowlist. SVG is absent on purpose, as is video. */
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 3;

/**
 * Screenshot picker for a report.
 *
 * Client-side checks here are a courtesy, not a control: they turn an obvious
 * mistake into an instant message instead of a round trip. The real validation
 * is server-side and reads the file's magic bytes, because `accept` and
 * `file.type` are both trivially bypassed.
 */
export function AttachmentPicker({
  files,
  onChange,
  onReject,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  onReject: (message: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    const room = MAX_FILES - files.length;
    const accepted: File[] = [];

    for (const file of picked.slice(0, room)) {
      if (file.size > MAX_BYTES) {
        onReject(t('feedback.attachments.tooLarge'));
        continue;
      }
      if (!ACCEPT.split(',').includes(file.type)) {
        onReject(t('feedback.attachments.rejected'));
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length) onChange([...files, ...accepted]);
    // Reset so picking the same file twice in a row still fires a change.
    event.target.value = '';
  };

  return (
    <div className="space-y-2">
      <div>
        <span className="block text-small font-medium text-foreground">
          {t('feedback.attachments.label')}
        </span>
        <span className="block text-micro text-muted">
          {t('feedback.attachments.hint')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          isDisabled={disabled || files.length >= MAX_FILES}
          onPress={() => inputRef.current?.click()}
          startIcon={<Paperclip className="size-4" />}
        >
          {t('feedback.attachments.choose')}
        </Button>
        <span className="text-micro text-muted">
          {t('feedback.attachments.max', { count: MAX_FILES })}
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={handlePick}
          disabled={disabled || files.length >= MAX_FILES}
          className="hidden"
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}`}
              className="flex items-center gap-2 rounded-lg bg-surface-1 px-2.5 py-2"
            >
              <ImageIcon aria-hidden className="size-4 shrink-0 text-muted" />
              <span className="min-w-0 flex-1 truncate text-small text-foreground">
                {file.name}
              </span>
              <span className="shrink-0 text-micro text-muted">
                {(file.size / 1024).toFixed(0)} KB
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((_, i) => i !== index))}
                aria-label={`${t('feedback.attachments.remove')} ${file.name}`}
                className="rounded-sm p-1 text-danger-600 outline-none hover:bg-danger-50 focus-visible:ring-2 focus-visible:ring-primary/60 disabled:opacity-50"
              >
                <Trash2 aria-hidden className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
